import { find } from 'lodash';
import { useSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMemo, useState, useEffect, type UIEvent } from 'react';

import { LoadingButton } from '@mui/lab';
import Grid from '@mui/material/Unstable_Grid2';
import { Box, Card, Chip, Stack, Button, Typography, ListItemText } from '@mui/material';

import { useSearchWithDebounce } from 'src/hooks/use-search-with-debounce';

import * as GQL from 'src/graphql/generated';
import { useLocales } from 'src/locales/use-locales';

import Iconify from 'src/components/iconify';
import Label from 'src/components/label/label';
import FormProvider from 'src/components/hook-form/form-provider';
import {
  RHFCheckbox,
  RHFTextField,
  RHFAutocomplete,
  RHFMaskTextField,
} from 'src/components/hook-form';

import {
  TYPES_VALUE,
  COUPON_SCOPE_MODE,
  COUPON_PRODUCT_SCOPE,
  ConfigFormGiftBackSchema,
  COUPON_SCOPE_MODE_OPTIONS,
  COUPON_PRODUCT_SCOPE_OPTIONS,
  ConfigFormGiftBackSchemaDefaultValues,
} from './config-form-schema';

// E-commerce integrations that support the cumulative discount feature.
// Add new integration keys here as they are onboarded.
const ECOMMERCE_INTEGRATIONS_WITH_CUMULATIVE = ['yampi'] as const;
const CATALOG_SEARCH_PAGE_SIZE = 20;

type CatalogOption = {
  label: string;
  value: number;
  integrationId?: string | null;
};

function mapCatalogOptions(
  items?: Array<
    { name?: string | null; pk?: number | null; integrationId?: string | null } | null | undefined
  >
) {
  return (
    items
      ?.filter((item): item is { name: string; pk: number; integrationId: string } =>
        Boolean(item?.name && item?.pk && item?.integrationId)
      )
      .map((item) => ({
        label: item.name,
        value: item.pk,
        integrationId: item.integrationId,
      })) || []
  );
}

function hasReachedListBottom(event: UIEvent<HTMLUListElement>) {
  const listbox = event.currentTarget;

  return listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 24;
}

function getCatalogOptionLabel(option: string | CatalogOption) {
  return typeof option === 'string' ? option : option.label;
}

function getCouponCatalogLabel(scopeValue?: string, modeValue?: string) {
  const isExcludeMode = modeValue === COUPON_SCOPE_MODE.EXCLUDE;

  if (scopeValue === COUPON_PRODUCT_SCOPE.PRODUCTS) {
    return isExcludeMode ? 'Produtos que não recebem cupom' : 'Produtos que recebem cupom';
  }

  if (scopeValue === COUPON_PRODUCT_SCOPE.CATEGORIES) {
    return isExcludeMode ? 'Categorias que não recebem cupom' : 'Categorias que recebem cupom';
  }

  return 'Itens selecionados';
}

// ----------------------------------------------------------------------

export default function ConfigFormGiftBack() {
  const [enableForm, setEnableForm] = useState<boolean>(false);
  const { currentLang } = useLocales();

  const methods = useForm({
    resolver: yupResolver(ConfigFormGiftBackSchema),
    defaultValues: ConfigFormGiftBackSchemaDefaultValues,
  });

  const { control } = methods;
  const { fields, append, remove } = useFieldArray({ name: 'multipleConfigs', control });

  const configGiftBack = GQL.useGetGiftbackConfigQuery();
  const { data: integrationData } = GQL.useGetStoreIntegrationsQuery();
  const [updateConfigGiftBack] = GQL.useUpdateGiftbackConfigMutation();
  const [productSearch, setProductSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [fetchCouponProducts, couponProductsResult] = GQL.useListAllProductsLazyQuery({
    notifyOnNetworkStatusChange: true,
  });
  const [fetchCouponCategories, couponCategoriesResult] = GQL.useCategoryListLazyQuery({
    notifyOnNetworkStatusChange: true,
  });
  const { handleSearch: handleSearchCouponProducts } = useSearchWithDebounce({
    refetch: fetchCouponProducts,
    searchVariable: 'name_Icontains',
    initialVariables: {
      first: CATALOG_SEARCH_PAGE_SIZE,
      isActivated: true,
    },
    debounceDelay: 500,
  });
  const { handleSearch: handleSearchCouponCategories } = useSearchWithDebounce({
    refetch: fetchCouponCategories,
    searchVariable: 'name_Icontains',
    initialVariables: {
      first: CATALOG_SEARCH_PAGE_SIZE,
    },
    debounceDelay: 500,
  });
  const [fetchTags, { data: tagsData }] = GQL.useGetAllCustomerTagsLazyQuery();
  const { handleSearch: handleSearchTags } = useSearchWithDebounce({
    refetch: fetchTags,
    searchVariable: 'name_Icontains',
  });
  const tagOptions = useMemo(
    () =>
      tagsData?.customerTagsList?.edges?.map?.((edge) => ({
        label: String(edge?.node?.name),
        value: String(edge?.node?.pk),
      })) || [],
    [tagsData]
  );

  const couponProductOptions = useMemo(
    () =>
      mapCatalogOptions(
        couponProductsResult.data?.allProductsRelay?.edges?.map((edge) => edge?.node) || []
      ),
    [couponProductsResult.data?.allProductsRelay?.edges]
  );
  const couponCategoryOptions = useMemo(
    () =>
      mapCatalogOptions(
        couponCategoriesResult.data?.categoryList?.edges?.map((edge) => edge?.node) || []
      ),
    [couponCategoriesResult.data?.categoryList?.edges]
  );

  const hasYampi =
    ECOMMERCE_INTEGRATIONS_WITH_CUMULATIVE.includes('yampi') &&
    Boolean((integrationData?.infoIntegration as any)?.yampistoreintegration?.connected);
  const showCumulativeCheckbox = hasYampi;
  const showCouponScopeConfig = Boolean(
    integrationData?.infoIntegration?.lojaintegradastoreintegration?.connected
  );
  const { enqueueSnackbar } = useSnackbar();

  const handleRemoveConfig = (index: number) => {
    if (fields.length <= 1) {
      enqueueSnackbar('É necessário ter no mínimo uma configuração ativa por loja.', {
        variant: 'warning',
      });
      return;
    }
    remove(index);
  };

  const handleLoadMoreProducts = () => {
    const pageInfo = couponProductsResult.data?.allProductsRelay?.pageInfo;

    if (!pageInfo?.hasNextPage || couponProductsResult.loading) {
      return;
    }

    couponProductsResult.fetchMore({
      variables: {
        after: pageInfo.endCursor,
        first: CATALOG_SEARCH_PAGE_SIZE,
        name_Icontains: productSearch,
        isActivated: true,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.allProductsRelay) {
          return previousResult;
        }

        return {
          ...previousResult,
          allProductsRelay: {
            ...fetchMoreResult.allProductsRelay,
            edges: [
              ...(previousResult.allProductsRelay?.edges || []),
              ...(fetchMoreResult.allProductsRelay.edges || []),
            ],
          },
        };
      },
    });
  };

  const handleLoadMoreCategories = () => {
    const pageInfo = couponCategoriesResult.data?.categoryList?.pageInfo;

    if (!pageInfo?.hasNextPage || couponCategoriesResult.loading) {
      return;
    }

    couponCategoriesResult.fetchMore({
      variables: {
        after: pageInfo.endCursor,
        first: CATALOG_SEARCH_PAGE_SIZE,
        name_Icontains: categorySearch,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.categoryList) {
          return previousResult;
        }

        return {
          ...previousResult,
          categoryList: {
            ...fetchMoreResult.categoryList,
            edges: [
              ...(previousResult.categoryList?.edges || []),
              ...(fetchMoreResult.categoryList.edges || []),
            ],
          },
        };
      },
    });
  };

  const { handleSubmit, reset, formState, watch } = methods;
  const watchedConfigs = watch('multipleConfigs');
  const watchedCouponProductScope = watch('couponProductScope');
  const watchedCouponScopeMode = watch('couponScopeMode');

  const onSubmit = handleSubmit(async (data) => {
    const multipleConfigs = (data.multipleConfigs || []).map((cfg) => {
      const isFixed =
        cfg.minimumValueType?.value === GQL.MultipleGiftbackConfigValueType.FixedValue;
      const isPercent =
        cfg.minimumValueType?.value === GQL.MultipleGiftbackConfigValueType.Percentage;
      return {
        id: cfg.id || undefined,
        valueType: cfg.valueType?.value as GQL.MultipleGiftbackConfigValueType,
        value: cfg.value ? Number(cfg.value) : 0,
        minPurchaseValueForCreate: cfg.minPurchaseValueForCreate
          ? Number(cfg.minPurchaseValueForCreate) / 100
          : 0,
        minimumValueType: cfg?.minimumValueType?.value as GQL.MultipleGiftbackConfigValueType,
        maximumDiscountPercentage:
          isPercent && cfg.maximumDiscountPercentage
            ? Number(cfg.maximumDiscountPercentage) / 100
            : 0,
        discountLimitEnabled: !!cfg.discountLimitEnabled,
        discountLimit: cfg.discountLimit ? Number(cfg.discountLimit) / 100 : 0,
        minPurchaseValueForRedeem:
          (isFixed || isPercent) && cfg.minPurchaseValueForRedeem
            ? Number(cfg.minPurchaseValueForRedeem) / 100
            : 0,
        tagsIds: (cfg.tags || []).map((tag) => String(tag.value)),
      };
    });

    const couponScopeModeValue = (
      data.couponScopeMode as { value?: string } | null | undefined
    )?.value;
    const couponScopeInput = showCouponScopeConfig
      ? {
          couponProductScope: data.couponProductScope?.value as any,
          couponScopeMode:
            data.couponProductScope?.value === COUPON_PRODUCT_SCOPE.ALL_PRODUCTS
              ? COUPON_SCOPE_MODE.INCLUDE
              : (couponScopeModeValue as any),
          couponScopeProductsIds:
            data.couponProductScope?.value === COUPON_PRODUCT_SCOPE.PRODUCTS
              ? data.couponScopeProducts?.map((product) => String(product.value)) || []
              : [],
          couponScopeCategoriesIds:
            data.couponProductScope?.value === COUPON_PRODUCT_SCOPE.CATEGORIES
              ? data.couponScopeCategories?.map((category) => String(category.value)) || []
              : [],
        }
      : {};

    const variables: GQL.UpdateGiftbackConfigMutationVariables = {
      input: {
        dueDate: Number(data.dueDate),
        ...(showCumulativeCheckbox && { cumulative: !!data.cumulative }),
        ...couponScopeInput,
        multipleConfigs,
      },
    };
    try {
      await updateConfigGiftBack({
        variables,
        refetchQueries: [{ query: GQL.GetGiftbackConfigDocument }],
        awaitRefetchQueries: true,
      });

      enqueueSnackbar('Dados atualizados!', { variant: 'success' });

      setEnableForm(false);
    } catch (error) {
      console.error(error);

      if (error?.message) {
        enqueueSnackbar(error?.message, { variant: 'error' });

        return;
      }
      enqueueSnackbar('Não foi possível atualizar o usuário!', { variant: 'error' });
    }
  });

  useEffect(() => {
    const updateFields = () => {
      if (configGiftBack.data?.getGiftbackConfig) {
        const {
          dueDate,
          cumulative,
          couponProductScope,
          couponScopeMode,
          couponScopeProducts,
          couponScopeCategories,
          multiplegiftbackconfigSet,
        } = configGiftBack.data.getGiftbackConfig;

        const multipleConfigs = (multiplegiftbackconfigSet || []).map((cfg) => {
          const foundValueType = find(TYPES_VALUE, ['value', cfg.valueType]);
          const foundMinimumValueType = find(TYPES_VALUE, ['value', cfg.minimumValueType]);

          return {
            id: cfg.id ?? '',
            valueType: foundValueType,
            value: String(cfg.value ?? ''),
            minPurchaseValueForCreate: cfg.minPurchaseValueForCreate
              ? String(cfg.minPurchaseValueForCreate * 100)
              : '',
            minimumValueType: foundMinimumValueType,
            maximumDiscountPercentage: cfg.maximumDiscountPercentage
              ? String(cfg.maximumDiscountPercentage * 100)
              : '',
            discountLimitEnabled: !!cfg.discountLimitEnabled,
            discountLimit: cfg.discountLimit ? String(cfg.discountLimit * 100) : '',
            minPurchaseValueForRedeem: cfg.minPurchaseValueForRedeem
              ? String(cfg.minPurchaseValueForRedeem * 100)
              : '',
            tags: (cfg.tags || []).map((tag) => ({
              label: String(tag?.name),
              value: String(tag?.id),
            })),
          };
        });

        reset({
          dueDate: String(dueDate ?? ''),
          cumulative: cumulative ?? false,
          couponProductScope:
            find(COUPON_PRODUCT_SCOPE_OPTIONS, ['value', couponProductScope]) ||
            COUPON_PRODUCT_SCOPE_OPTIONS[0],
          couponScopeMode:
            find(COUPON_SCOPE_MODE_OPTIONS, ['value', couponScopeMode]) ||
            COUPON_SCOPE_MODE_OPTIONS[0],
          couponScopeProducts: mapCatalogOptions(couponScopeProducts || []),
          couponScopeCategories: mapCatalogOptions(couponScopeCategories || []),
          multipleConfigs: multipleConfigs.length
            ? multipleConfigs
            : ConfigFormGiftBackSchemaDefaultValues.multipleConfigs,
        });
      }
    };

    updateFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configGiftBack.data?.getGiftbackConfig, enableForm]);

  const couponScopeValue = watchedCouponProductScope?.value;
  const showCouponProducts =
    showCouponScopeConfig && couponScopeValue === COUPON_PRODUCT_SCOPE.PRODUCTS;
  const showCouponCategories =
    showCouponScopeConfig && couponScopeValue === COUPON_PRODUCT_SCOPE.CATEGORIES;
  const showCouponScopeMode = showCouponProducts || showCouponCategories;
  const couponScopeModeValue = (
    watchedCouponScopeMode as { value?: string } | null | undefined
  )?.value;
  const couponCatalogLabel = getCouponCatalogLabel(
    couponScopeValue,
    couponScopeModeValue
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit!}>
      <Grid container spacing={3}>
        <Grid xs={12} gap={3} display="flex" flexDirection="column">
          <ListItemText
            primary="Configure o Giftback de acordo com a sua empresa!"
            secondary={
              <>
                Personalize seus giftbacks escolhendo entre descontos fixos em{' '}
                {currentLang.currencyNamePlural} ({currentLang.currencyShort}) ou percentuais (%)
                nas vendas.
                <br /> <br />
                Por exemplo, ofereça um giftback com desconto de {currentLang.currencyShort}10,00 ou
                10% do valor venda.
                <br /> <br />
                Defina a validade do Giftback e ajuste as configurações de vendas para criação e
                resgate do Giftback.
              </>
            }
            primaryTypographyProps={{ typography: 'h5', mb: 0.5, component: 'h2' }}
            secondaryTypographyProps={{ component: 'span' }}
            sx={{
              pr: 3,
              pl: 3,
            }}
          />
          <Card sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <RHFTextField
                disabled={!enableForm}
                name="dueDate"
                label="Tempo ativo"
                helperText={
                  <Stack component="span" direction="row" alignItems="center">
                    <Iconify icon="eva:info-fill" width={16} sx={{ mr: 0.5 }} />
                    Quantos dias corridos o giftback vai ficar ativo.
                  </Stack>
                }
              />
            </Box>

            {showCumulativeCheckbox && (
              <Box sx={{ mb: 3 }}>
                <RHFCheckbox
                  disabled={!enableForm}
                  name="cumulative"
                  label="Habilitar desconto acumulativo com outras promoções"
                  sx={{ ml: 0 }}
                />
              </Box>
            )}

            {showCouponScopeConfig && (
              <Box sx={{ mb: 3 }}>
                <ListItemText
                  primary="Produtos e categorias do cupom"
                  primaryTypographyProps={{ typography: 'subtitle1', mb: 2 }}
                />
                <Box
                  rowGap={3}
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: showCouponScopeMode ? 'repeat(2, 1fr)' : '1fr',
                  }}
                >
                  <RHFAutocomplete
                    disabled={!enableForm}
                    name="couponProductScope"
                    label="Uso do cupom"
                    options={COUPON_PRODUCT_SCOPE_OPTIONS}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  />

                  {showCouponScopeMode && (
                    <RHFAutocomplete
                      disabled={!enableForm}
                      name="couponScopeMode"
                      label="Aplicar seleção como"
                      options={COUPON_SCOPE_MODE_OPTIONS}
                      isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    />
                  )}

                  {showCouponProducts && (
                    <RHFAutocomplete
                      multiple
                      filterSelectedOptions
                      disabled={!enableForm}
                      name="couponScopeProducts"
                      label={couponCatalogLabel}
                      sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}
                      options={couponProductOptions}
                      loading={couponProductsResult.loading}
                      filterOptions={(options) => options}
                      getOptionLabel={getCatalogOptionLabel}
                      isOptionEqualToValue={(option, value) => option?.value === value?.value}
                      onInputChange={(event, value, reason) => {
                        if (reason === 'reset') {
                          return;
                        }

                        setProductSearch(value);
                        handleSearchCouponProducts(event, value);
                      }}
                      ListboxProps={{
                        onScroll: (event) => {
                          if (hasReachedListBottom(event)) {
                            handleLoadMoreProducts();
                          }
                        },
                      }}
                      renderOption={(props, option: CatalogOption) => (
                        <li {...props} key={`${option.value}-${option.integrationId || ''}`}>
                          <ListItemText
                            primary={option.label}
                            secondary={
                              option.integrationId
                                ? `ID Loja Integrada: ${option.integrationId}`
                                : `#${option.value}`
                            }
                          />
                        </li>
                      )}
                    />
                  )}

                  {showCouponCategories && (
                    <RHFAutocomplete
                      multiple
                      filterSelectedOptions
                      disabled={!enableForm}
                      name="couponScopeCategories"
                      label={couponCatalogLabel}
                      sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}
                      options={couponCategoryOptions}
                      loading={couponCategoriesResult.loading}
                      filterOptions={(options) => options}
                      getOptionLabel={getCatalogOptionLabel}
                      isOptionEqualToValue={(option, value) => option?.value === value?.value}
                      onInputChange={(event, value, reason) => {
                        if (reason === 'reset') {
                          return;
                        }

                        setCategorySearch(value);
                        handleSearchCouponCategories(event, value);
                      }}
                      ListboxProps={{
                        onScroll: (event) => {
                          if (hasReachedListBottom(event)) {
                            handleLoadMoreCategories();
                          }
                        },
                      }}
                      renderOption={(props, option: CatalogOption) => (
                        <li {...props} key={`${option.value}-${option.integrationId || ''}`}>
                          <ListItemText
                            primary={option.label}
                            secondary={
                              option.integrationId
                                ? `ID Loja Integrada: ${option.integrationId}`
                                : `#${option.value}`
                            }
                          />
                        </li>
                      )}
                    />
                  )}
                </Box>
              </Box>
            )}

            {fields.map((field, index) => {
              const cfg = watchedConfigs?.[index];
              const minType = cfg?.minimumValueType?.value as
                | GQL.MultipleGiftbackConfigValueType
                | undefined;
              const isFixedValue = minType === GQL.MultipleGiftbackConfigValueType.FixedValue;
              const isPercentage = minType === GQL.MultipleGiftbackConfigValueType.Percentage;
              const discountLimitEnabled = !!cfg?.discountLimitEnabled;
              return (
                <Box
                  key={field.id}
                  rowGap={3}
                  columnGap={2}
                  display="grid"
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  }}
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: 'divider',
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start"
                    sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}
                  >
                    <Chip
                      label={`Configuração ${index + 1}`}
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  </Stack>

                  <Stack display="flex" flexDirection="row" gap="0.5rem">
                    <RHFAutocomplete
                      disabled={!enableForm}
                      name={`multipleConfigs.${index}.valueType`}
                      label="Tipo"
                      options={TYPES_VALUE}
                    />
                    <RHFTextField
                      disabled={!enableForm}
                      name={`multipleConfigs.${index}.value`}
                      label="Valor"
                      helperText={
                        <Stack component="span" direction="row" alignItems="center">
                          <Iconify icon="eva:info-fill" width={16} sx={{ mr: 0.5 }} />
                          Desconto do Giftback em{' '}
                          {currentLang.currencyShort === 'R$' ? 'Real' : 'Dollar'} (
                          {currentLang.currencyShort}) ou Percentual (%).
                        </Stack>
                      }
                    />
                  </Stack>
                  <RHFMaskTextField
                    disabled={!enableForm}
                    name={`multipleConfigs.${index}.minPurchaseValueForCreate`}
                    label="Valor mínimo para gerar"
                    helperText={
                      <Stack component="span" direction="row" alignItems="center">
                        <Iconify icon="eva:info-fill" width={16} sx={{ mr: 0.5 }} />
                        Valor mínimo da venda para gerar o giftback.
                      </Stack>
                    }
                    formatMask="MONEY"
                  />
                  <Stack display="flex" flexDirection="row" gap="0.5rem">
                    <RHFAutocomplete
                      disabled={!enableForm}
                      name={`multipleConfigs.${index}.minimumValueType`}
                      label="Tipo"
                      options={TYPES_VALUE}
                    />
                    {isFixedValue && (
                      <RHFMaskTextField
                        disabled={!enableForm}
                        name={`multipleConfigs.${index}.minPurchaseValueForRedeem`}
                        label="Valor mínimo para resgatar"
                        helperText={
                          <Stack component="span" direction="row" alignItems="center">
                            <Iconify icon="eva:info-fill" width={16} sx={{ mr: 0.5 }} />
                            Valor mínimo da venda para resgatar o giftback.
                          </Stack>
                        }
                        formatMask="MONEY"
                      />
                    )}
                    {isPercentage && (
                      <RHFMaskTextField
                        disabled={!enableForm}
                        name={`multipleConfigs.${index}.maximumDiscountPercentage`}
                        label="Percentual máximo de desconto"
                        helperText={
                          <Stack component="span" direction="row" alignItems="center">
                            <Iconify icon="eva:info-fill" width={16} sx={{ mr: 0.5 }} />
                            Valor máximo que o cupom pode representar na próxima compra
                          </Stack>
                        }
                        formatMask="PORCENTAGEM"
                      />
                    )}
                  </Stack>
                  <Stack display="flex" flexDirection="row" gap="0.5rem" alignItems="center">
                    <RHFCheckbox
                      disabled={!enableForm}
                      name={`multipleConfigs.${index}.discountLimitEnabled`}
                      label="Habilitar Desconto máximo"
                    />
                    <RHFMaskTextField
                      disabled={!discountLimitEnabled || !enableForm}
                      name={`multipleConfigs.${index}.discountLimit`}
                      label="Valor máximo do giftback"
                      helperText={
                        <Stack component="span" direction="row" alignItems="center">
                          <Iconify icon="eva:info-fill" width={16} sx={{ mr: 0.5 }} />
                          Valor máximo que um giftback pode ter
                        </Stack>
                      }
                      formatMask="MONEY"
                    />
                  </Stack>
                  {enableForm ? (
                    <RHFAutocomplete
                      multiple
                      disableCloseOnSelect
                      name={`multipleConfigs.${index}.tags`}
                      label="Tags de clientes (opcional)"
                      placeholder="Aplica pra todos os clientes se vazio"
                      options={tagOptions}
                      onInputChange={handleSearchTags}
                      sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}
                    />
                  ) : (
                    <Card
                      sx={{
                        minHeight: 53,
                        p: '1rem',
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                        gridColumn: { xs: 'span 1', sm: 'span 2' },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Tags
                      </Typography>
                      {cfg?.tags?.length ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            flexWrap: 'wrap',
                            gap: '8px',
                          }}
                        >
                          {cfg.tags.map((tag) => (
                            <Label key={tag?.value} variant="soft" color="info">
                              {tag?.label}
                            </Label>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          Nenhuma tag atribuída
                        </Typography>
                      )}
                    </Card>
                  )}
                  {enableForm && (
                    <Box
                      gridColumn={{ xs: 'span 1', sm: 'span 2' }}
                      display="flex"
                      justifyContent="flex-end"
                      sx={{ mt: 1 }}
                    >
                      <Button
                        color="error"
                        variant="text"
                        onClick={() => handleRemoveConfig(index)}
                      >
                        Remover configuração
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })}

            {enableForm && (
              <Box display="flex" justifyContent="flex-start" sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:plus-fill" />}
                  onClick={() =>
                    append({
                      ...ConfigFormGiftBackSchemaDefaultValues.multipleConfigs[0],
                    })
                  }
                >
                  Adicionar configuração
                </Button>
              </Box>
            )}
            <Box>
              {enableForm && (
                <Stack
                  flexDirection="row"
                  justifyContent="flex-end"
                  alignItems="flex-end"
                  sx={{ mt: 3, gap: 1 }}
                >
                  <Button variant="outlined" color="error" onClick={() => setEnableForm(false)}>
                    Cancelar
                  </Button>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    loading={formState.isSubmitting}
                  >
                    Salvar
                  </LoadingButton>
                </Stack>
              )}
              {!enableForm && (
                <Stack display="flex" flexDirection="row" justifyContent="flex-end">
                  <Button variant="contained" color="primary" onClick={() => setEnableForm(true)}>
                    Editar
                  </Button>
                </Stack>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
